import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import i18next from "i18next";
import { buildThemeClasses } from "../../nuvex-access/theme";
import { useBasesStore, type Booking } from "../store";
import BookingFormModal, { type BookingDraft } from "../components/BookingFormModal";
import {
  generateInvoicePdf,
  buildGoogleCalendarUrl,
  downloadIcsInvite,
  buildConfirmationMailto,
} from "../services/bookingFlow";

import { DIALOG_COLORS } from "../../../components/ui";
// ---------------------------------------------------------------------------
// Reservaciones — calendar of who/when + staff booker. Confirming a booking
// runs the downstream flow: invoice (FAC-YYYY-#####) + Google Calendar invite
// + email, just like checking out a seat/POS order.
// ---------------------------------------------------------------------------

const statusTone: Record<Booking["status"], string> = {
  Confirmada: "bg-emerald-500/15 text-emerald-500",
  "Por confirmar": "bg-amber-500/15 text-amber-500",
  Cancelada: "bg-red-500/15 text-red-500",
};

const payTone: Record<Booking["paymentStatus"], string> = {
  Pagado: "bg-emerald-500/15 text-emerald-500",
  Pendiente: "bg-amber-500/15 text-amber-500",
  "No requerido": "bg-gray-400/15 text-gray-500",
};

export default function ReservasList() {
  const { t } = useTranslation("common");
  const tc = buildThemeClasses();

  const amenities = useBasesStore((s) => s.amenities);
  const bookings = useBasesStore((s) => s.bookings);
  const addBooking = useBasesStore((s) => s.addBooking);
  const updateBooking = useBasesStore((s) => s.updateBooking);
  const removeBooking = useBasesStore((s) => s.removeBooking);
  const bookingsHasMore = useBasesStore((s) => s.bookingsHasMore);
  const bookingsRange = useBasesStore((s) => s.bookingsRange);
  const companyName =
    localStorage.getItem("companyName") || t("categories.bases-y-propiedades.title");
  // The logged-in agent that books on behalf of the customer (staff booker).
  const agentName =
    localStorage.getItem("userName") ||
    localStorage.getItem("name") ||
    t("basesPropiedades.booking.staff");

  const [showBooker, setShowBooker] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...bookings].sort((a, b) => (a.date < b.date ? 1 : -1));
    if (!q) return sorted;
    return sorted.filter(
      (b) =>
        b.customerName.toLowerCase().includes(q) ||
        b.amenityName.toLowerCase().includes(q),
    );
  }, [bookings, query]);

  // Confirm flow shared with the public page: persist booking, allocate
  // invoice, then offer the invoice PDF + calendar + email artifacts.
  const handleConfirm = async (draft: BookingDraft) => {
    // El servidor valida el horario (sin encimes) y asigna el folio de factura.
    const paymentStatus = draft.price > 0 ? "Pagado" : "No requerido";
    let booking: Booking;
    try {
      booking = await addBooking({ ...draft, paymentStatus }, true);
    } catch (err) {
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      void Swal.fire({ icon: "error", title: msg || i18next.t("bases:common.saveError"), confirmButtonColor: DIALOG_COLORS.primary });
      return;
    }
    const invoiceNumber = booking.invoiceNumber ?? "";
    if (draft.price > 0 && invoiceNumber && !booking.paymentRef) {
      booking = await updateBooking(booking.id, { paymentRef: `PAY-${invoiceNumber.replace("FAC-", "")}` });
    }
    setShowBooker(false);

    const amenity = amenities.find((a) => a.id === booking.amenityId);
    Swal.fire({
      title: t("basesPropiedades.booking.confirmedTitle"),
      html: `${t("basesPropiedades.booking.confirmedBody", {
        invoice: invoiceNumber,
      })}`,
      icon: "success",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: t("basesPropiedades.booking.downloadInvoice"),
      denyButtonText: t("basesPropiedades.booking.addToCalendar"),
      cancelButtonText: t("basesPropiedades.booking.sendEmail"),
      confirmButtonColor: DIALOG_COLORS.primary,
    }).then((res) => {
      if (res.isConfirmed) {
        generateInvoicePdf(booking, amenity, companyName);
        downloadIcsInvite(booking);
      } else if (res.isDenied) {
        window.open(buildGoogleCalendarUrl(booking), "_blank");
      } else if (res.dismiss === Swal.DismissReason.cancel) {
        window.location.href = buildConfirmationMailto(booking);
      }
    });
  };

  const downloadInvoice = async (b: Booking) => {
    let booking = b;
    if (!booking.invoiceNumber) booking = await updateBooking(b.id, {}, true);
    generateInvoicePdf(
      booking,
      amenities.find((a) => a.id === booking.amenityId),
      companyName,
    );
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-calendar-check-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>
                {t("basesPropiedades.reservas.listTitle")}
              </h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {t("basesPropiedades.reservas.listSubtitle")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}
            >
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("basesPropiedades.search")}
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              onClick={() => setShowBooker(true)}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"
            >
              <i className="ri-user-add-line" />
              <span className="whitespace-nowrap">
                {t("basesPropiedades.reservas.staffBooker")}
              </span>
            </button>
          </div>
        </div>

        {bookingsHasMore ? (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-warning/30 bg-warning/10 px-4 py-2.5 text-sm text-warning">
            <i className="ri-information-line" />
            {i18next.t("bases:bookings.hasMore", { from: bookingsRange.from ?? "", to: bookingsRange.to ?? "", interpolation: { escapeValue: false } })}
          </div>
        ) : null}

        <div className={`overflow-x-auto rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b text-left ${tc.border} ${tc.textSecondary}`}>
                <th className="px-4 py-3 font-medium">
                  {t("basesPropiedades.fields.amenity")}
                </th>
                <th className="px-4 py-3 font-medium">
                  {t("basesPropiedades.fields.customer")}
                </th>
                <th className="px-4 py-3 font-medium">
                  {t("basesPropiedades.fields.date")}
                </th>
                <th className="px-4 py-3 font-medium">
                  {t("basesPropiedades.fields.schedule")}
                </th>
                <th className="px-4 py-3 text-center font-medium">
                  {t("basesPropiedades.fields.channel")}
                </th>
                <th className="px-4 py-3 text-center font-medium">
                  {t("basesPropiedades.fields.status")}
                </th>
                <th className="px-4 py-3 text-center font-medium">
                  {t("basesPropiedades.fields.payment")}
                </th>
                <th className="px-4 py-3 text-center font-medium">
                  {t("basesPropiedades.fields.invoice")}
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className={`border-b ${tc.border}`}>
                  <td className={`px-4 py-3 ${tc.text}`}>{b.amenityName}</td>
                  <td className={`px-4 py-3 ${tc.text}`}>
                    <div>{b.customerName}</div>
                    {b.agentName && (
                      <div className={`text-xs ${tc.textSecondary}`}>
                        <i className="ri-user-star-line mr-1" />
                        {t("basesPropiedades.booking.bookedBy", {
                          agent: b.agentName,
                        })}
                      </div>
                    )}
                  </td>
                  <td className={`px-4 py-3 ${tc.text}`}>{b.date}</td>
                  <td className={`px-4 py-3 ${tc.text}`}>
                    {b.startTime}–{b.endTime}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`rounded-full px-2 py-1 text-xs ${tc.textSecondary}`}>
                      {b.channel === "staff"
                        ? t("basesPropiedades.booking.staff")
                        : t("basesPropiedades.booking.public")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`rounded-full px-2 py-1 text-xs ${statusTone[b.status]}`}>
                      {t(`basesPropiedades.bookingStatus.${b.status}`, b.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`rounded-full px-2 py-1 text-xs ${payTone[b.paymentStatus]}`}>
                      {t(`basesPropiedades.paymentStatus.${b.paymentStatus}`, b.paymentStatus)}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-center text-xs ${tc.textSecondary}`}>
                    {b.invoiceNumber ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        title={t("basesPropiedades.booking.downloadInvoice")}
                        onClick={() => downloadInvoice(b)}
                        className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                      >
                        <i className="ri-file-download-line" />
                      </button>
                      <button
                        title={t("basesPropiedades.booking.addToCalendar")}
                        onClick={() => window.open(buildGoogleCalendarUrl(b), "_blank")}
                        className="rounded-md p-1.5 text-sky-500 hover:bg-sky-500/10"
                      >
                        <i className="ri-calendar-line" />
                      </button>
                      <button
                        title={t("basesPropiedades.cancel")}
                        onClick={() => void removeBooking(b.id)}
                        className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className={`px-4 py-12 text-center text-sm ${tc.textSecondary}`}
                  >
                    {t("basesPropiedades.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className={`mt-4 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {t("basesPropiedades.mockNotice")}
        </p>
      </div>

      {showBooker && (
        <BookingFormModal
          amenities={amenities}
          bookings={bookings}
          channel="staff"
          themeClasses={tc}
          agentName={agentName}
          embedded
          onCancel={() => setShowBooker(false)}
          onSubmit={handleConfirm}
        />
      )}
    </div>
  );
}
